import { faCalendar, faClock, faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledIcon from 'src/components/StyledIcon';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import TeamMemberAvatar from 'src/components/TeamMemberAvatar';
import { TeamMemberStatuses } from 'src/constants/TeamRoles';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { useVisitScheduleDisplay } from 'src/hooks/useVisitScheduleDisplay';
import { getTeamMemberDisplayName } from 'src/utils/nameUtils';

export const Route = createFileRoute('/(app)/onboarding/visit-saved')({
    component: VisitSavedComponent,
});

function VisitSavedComponent() {
    const { spacing } = useTheme();
    const { getTeamMemberDetails } = useTeamMemberData();
    const { nextStep, progress, createdVisitSchedule } = useOnboardingWorkflow();

    const caregiver = getTeamMemberDetails(createdVisitSchedule?.caregiverRid || '');

    const { startDateString, timeRangeString, repeatingDays } =
        useVisitScheduleDisplay(createdVisitSchedule);

    return (
        <>
            <PageHeader hideBackButton hideActionButton>
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            {createdVisitSchedule ? (
                <Stack sx={{ gap: 2.5, mb: 2.5 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        Visit Saved
                    </Typography>
                    {caregiver && (
                        <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                            <TeamMemberAvatar teamMember={caregiver} size="medium" />
                            <Stack>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {getTeamMemberDisplayName(caregiver)}
                                </Typography>
                                {caregiver.teamMemberStatus === TeamMemberStatuses.Invited && (
                                    <Typography variant="subtitle2">Pending Invitation</Typography>
                                )}
                            </Stack>
                        </Stack>
                    )}

                    <Stack
                        sx={{
                            border: '1px solid',
                            borderColor: 'gray.300',
                            borderRadius: spacing(1.5),
                        }}
                        divider={<Divider />}
                    >
                        {/* Date */}
                        {startDateString && (
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2.5,
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2">Date</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {startDateString}
                                    </Typography>
                                </Box>
                                <StyledIcon icon={faCalendar} />
                            </Stack>
                        )}

                        {/* Time */}
                        {timeRangeString && (
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2.5,
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2">Time</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {timeRangeString}
                                    </Typography>
                                </Box>
                                <StyledIcon icon={faClock} />
                            </Stack>
                        )}

                        {/* Repeating Days */}
                        {repeatingDays && (
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2.5,
                                    gap: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2">Repeating Days</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {repeatingDays}
                                    </Typography>
                                </Box>
                                <StyledIcon icon={faRepeat} />
                            </Stack>
                        )}

                        {/* TODO: add Address & Access Details */}
                    </Stack>
                    <Typography variant="body2">
                        Sage Home will remind your caregiver to fill out a summary of each care
                        visit. This reminder will be sent 20 minutes before each visit is scheduled
                        to end. You will receive their notes once they have been submitted.
                    </Typography>
                    <Typography variant="body2">
                        Make sure you have notifications enabled to be alerted when new notes are
                        submitted. You can do that on your account page.
                    </Typography>
                </Stack>
            ) : (
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="body1">Visit schedule not found.</Typography>
                </Stack>
            )}

            <PageFooter>
                <Button variant="contained" color="primary" fullWidth onClick={nextStep}>
                    Next
                </Button>
            </PageFooter>
        </>
    );
}
